import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/PageHeader';
import usePageSearchIndex from '../hooks/usePageSearchIndex';
import './AdministrativeStructure.css';

const AdministrativeStructure = () => {
  const { isMarathi } = useLanguage();

  usePageSearchIndex({
    mr: ['प्रशासकिय रचना', 'कल्याण आयुक्त', 'उप कल्याण आयुक्त', 'संघटन रचना'],
    en: ['administrative structure', 'welfare commissioner', 'deputy welfare commissioner', 'organizational structure']
  });

  const pageHeaderData = {
    title: {
      marathi: 'प्रशासकिय रचना',
      english: 'Administrative Structure'
    },
    subtitle: {
      marathi: 'महाराष्ट्र कामगार कल्याण मंडळाची प्रशासकिय रचना',
      english: 'Administrative Structure of Maharashtra Labour Welfare Board'
    }
  };

  const content = {
    marathi: {
      description: 'महाराष्ट्र कामगार कल्याण मंडळाची प्रशासकिय रचना खालील आकृतीमध्ये दर्शविली आहे:',
      lastUpdated: 'शेवटचे अपडेट: 16 सप्टेंबर 2025'
    },
    english: {
      description: 'The administrative structure of Maharashtra Labour Welfare Board is shown in the following diagram:',
      lastUpdated: 'Last Updated: September 16, 2025'
    }
  };

  const currentContent = isMarathi() ? content.marathi : content.english;

  return (
    <div className="administrative-structure-page">
      <div className="administrative-structure-container">
        {/* Page Header */}
        <PageHeader 
          title={pageHeaderData.title}
          subtitle={pageHeaderData.subtitle}
        />

        <div className="administrative-structure-content" data-read-section="admin-structure-content">
          <p className="administrative-structure-description">
            {currentContent.description}
          </p>
          
          <div className="administrative-structure-image-container">
            <img 
              src={isMarathi() 
                ? "https://mlwb.in/react-static-files/images/administrative-structure/prashaskiy-rachana-marathi.webp"
                : "https://mlwb.in/react-static-files/images/administrative-structure/administrative-structure-english.webp"
              }
              alt={isMarathi() ? 'प्रशासकिय रचना' : 'Administrative Structure'}
              className="administrative-structure-image"
              loading="lazy"
            />
          </div>
        </div>

        {/* Hidden ARIA Tree Structure for Screen Readers */}
        <div 
          role="tree" 
          aria-label={isMarathi() ? 'महाराष्ट्र कामगार कल्याण मंडळ संघटन रचना' : 'Maharashtra Labour Welfare Board Organizational Structure'}
          className="sr-only"
          data-read-section="admin-structure-tree"
        >
          <div role="treeitem" aria-expanded="true" aria-selected="false" tabIndex={0}>
            {isMarathi() ? 'कल्याण आयुक्त' : 'Welfare Commissioner'}
            <div role="group">
              <div role="treeitem" aria-expanded="true" aria-selected="false" tabIndex={-1}>
                {isMarathi() ? 'अतिरिक्त कल्याण आयुक्त' : 'Additional Welfare Commissioner'}
                <div role="group">
                  <div role="treeitem" aria-expanded="true" aria-selected="false" tabIndex={-1}>
                    {isMarathi() ? 'उप कल्याण आयुक्त – प्रशासन' : 'Deputy Welfare Commissioner – Administration'}
                    <div role="group">
                      <div role="treeitem" aria-selected="false" tabIndex={-1}>
                        {isMarathi() ? 'प्रशासकीय अधिकारी' : 'Administrative Officer'}
                      </div>
                    </div>
                  </div>

                  <div role="treeitem" aria-expanded="true" aria-selected="false" tabIndex={-1}>
                    {isMarathi() ? 'उप कल्याण आयुक्त – कार्यक्रम' : 'Deputy Welfare Commissioner – Programme'}
                    <div role="group">
                      <div role="treeitem" aria-selected="false" tabIndex={-1}>
                        {isMarathi() ? 'सहाय्यक कल्याण आयुक्त – कार्यक्रम व वाहन' : 'Assistant Welfare Commissioner – Programme and Vehicle'}
                      </div>
                    </div>
                  </div>

                  <div role="treeitem" aria-expanded="true" aria-selected="false" tabIndex={-1}>
                    {isMarathi() ? 'उप कल्याण आयुक्त – लेखा व लेखापरीक्षण' : 'Deputy Welfare Commissioner – Accounts and Audit'}
                    <div role="group">
                      <div role="treeitem" aria-selected="false" tabIndex={-1}>
                        {isMarathi() ? 'लेखा व लेखापरीक्षण अधिकारी' : 'Accounts and Audit Officer'}
                      </div>
                      <div role="treeitem" aria-selected="false" tabIndex={-1}>
                        {isMarathi() ? 'लेखापरीक्षण अधिकारी' : 'Audit Officer'}
                      </div>
                      <div role="treeitem" aria-selected="false" tabIndex={-1}>
                        {isMarathi() ? 'लेखा अधिकारी' : 'Accounts Officer'}
                      </div>
                    </div>
                  </div>

                  <div role="treeitem" aria-selected="false" tabIndex={-1}>
                    {isMarathi() ? 'जनसंपर्क अधिकारी' : 'Public and Relation Officer'}
                  </div>

                  <div role="treeitem" aria-selected="false" tabIndex={-1}>
                    {isMarathi() ? 'सहाय्यक कल्याण आयुक्त – महसूल' : 'Assistant Welfare Commissioner – Revenue'}
                  </div>

                  <div role="treeitem" aria-selected="false" tabIndex={-1}>
                    {isMarathi() ? 'स्थापत्य अभियंता' : 'Civil Engineer'}
                  </div>

                  <div role="treeitem" aria-selected="false" tabIndex={-1}>
                    {isMarathi() ? 'कायदे अधिकारी' : 'Legal Officer'}
                  </div>

                  <div role="treeitem" aria-selected="false" tabIndex={-1}>
                    {isMarathi() ? 'सहाय्यक कल्याण आयुक्त – संगणक' : 'Assistant Welfare Commissioner – Computer'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdministrativeStructure;
